import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Droplets, Palette, Pipette, Target, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { trackMetaEvent } from '../lib/metaPixel';
import { trackGAEvent } from '../lib/googleAnalytics';

const CustomMix = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const whatsappNumber = "+351913323197";
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(language === 'pt' ? 'Olá! Gostaria de igualar uma cor.' : 'Hello! I would like to have a colour matched.')}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, subject: 'Pedido de Mistura Personalizada', source: 'mistura' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success('Pedido recebido, obrigado! Vamos preparar a sua cor e entramos em contacto muito em breve. Enviámos-lhe também um email de confirmação.', { duration: 7000 });
        trackMetaEvent('Lead', { content_name: 'Pedido de Mistura' });
        trackGAEvent('generate_lead', { form_name: 'Pedido de Mistura' });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(data.error || 'Não foi possível enviar. Tente novamente ou ligue-nos.');
      }
    } catch {
      toast.error('Não foi possível enviar. Verifique a ligação e tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const features = [
    { icon: Target, text: t('customMix.features.0') },
    { icon: Palette, text: t('customMix.features.1') },
    { icon: Droplets, text: t('customMix.features.2') },
    { icon: Pipette, text: t('customMix.features.3') }
  ];

  // A "Simulação de Ambiente por Imagem" saiu daqui a 07/09/2026, por decisão da
  // Licínia: prometia que a loja fazia à mão uma simulação por cada foto recebida,
  // e ninguém a fazia. Uma promessa por cumprir num site é pior do que não haver
  // promessa nenhuma. O cartão que ficou mantém a rota de WhatsApp e os eventos de
  // medição, e só diz o que a loja faz mesmo.
  const zapTitulo = language === 'pt' ? 'Fale connosco por WhatsApp' : 'Talk to us on WhatsApp';
  const zapTexto = language === 'pt'
    ? 'Diga-nos a cor que quer igualar e nós tratamos. Se puder passar pela loja com uma amostra, melhor ainda: a máquina lê a cor e fazemo-la à sua medida.'
    : 'Tell us the colour you want matched and we will take care of it. Even better, bring a sample to the store: our machine reads the colour and we mix it to order.';

  return (
    <section id="custom-mix" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-orange-500/20 text-orange-400 text-sm font-semibold rounded-full mb-4">
            {t('customMix.title')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('customMix.subtitle')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Info */}
          <div className="space-y-8">
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('customMix.description')}
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-white font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Cartão de WhatsApp (era a "Simulação de Ambiente por Imagem", retirada 07/09/2026) */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{zapTitulo}</h3>
                  <p className="text-gray-300 text-sm">{zapTexto}</p>
                </div>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackMetaEvent('Contact', { content_name: 'WhatsApp Mistura' });
                    trackGAEvent('contact', { method: 'WhatsApp Mistura', pagina: 'mistura-a-medida' });
                  }}
                  className="flex-shrink-0 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-all duration-300 hover:scale-110 shadow-lg"
                  aria-label={zapTitulo}
                >
                  <MessageCircle className="w-7 h-7 text-white" />
                </a>
              </div>

              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackMetaEvent('Contact', { content_name: 'WhatsApp Mistura' });
                  trackGAEvent('contact', { method: 'WhatsApp Mistura', pagina: 'mistura-a-medida' });
                }}
                className="mt-4 flex items-center justify-center gap-2 text-green-400 font-bold hover:text-green-300 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                <span>+351 913 323 197</span>
              </a>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('customMix.formTitle')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/90">
                  {t('customMix.form.name')}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-orange-500"
                  placeholder="Seu nome"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/90">
                  {t('customMix.form.email')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-orange-500"
                  placeholder="seu@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/90">
                  {t('customMix.form.phone')}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-orange-500"
                  placeholder="+351 912 345 678"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-white/90">
                  {t('customMix.form.message')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-orange-500 resize-none"
                  placeholder="Descreva o que precisa..."
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg rounded-xl shadow-lg shadow-orange-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-orange-600/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? 'A enviar...' : t('customMix.form.submit')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomMix;
